/**
 * useMLeaderStyleEditorDialog — Track 3 Phase 3
 *
 * Owns the MultileaderStyleEditorDialog feature extracted from `App.tsx`
 * as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Mirrors {@link useDimStyleEditorDialog}; the only structural
 * difference is that mleader supports `onDeleteStyle` (dimstyle keeps
 * delete inside its own table view).
 *
 * Responsibilities:
 *   - Owns `isOpen` flag, `open()` / `close()` callbacks, and
 *     `editingMleaderStyleName` for "browse other styles".
 *   - Resolves the current style by `editingMleaderStyleName` →
 *     `propertyDraft["Multileader style"]` → first style.
 *   - Wires onSave / onCreateStyle / onDeleteStyle / onSelectStyle /
 *     onAnnotativePropagation handlers to store actions.
 *   - Closing clears `editingMleaderStyleName` (matches App.tsx:5359).
 *
 * Returned shape matches `useDimStyleEditorDialog` plus `onDeleteStyle`.
 *
 * AutoCAD parity:
 *   The MLEADERSTYLE command opens the Multileader Style Manager. The
 *   dialog component preserves the full tabbed UI; the hook only
 *   orchestrates open/close, current-style resolution, and store
 *   binding.
 */

import { useCallback, useMemo, useState } from "react";

import { useDialogOpenState } from "./useDialogOpenState";

import type {
  MultileaderStyle,
  TextStyle,
} from "@dxf-ai-editor/drawing-model";

import { useEditorStore } from "../state/editorStore";
import type { AnnotativeStylePropagationHandler } from "./styleEditorDialogTypes";

// Phase 1.k (П35Р React #185 fix) — stable sentinel empty arrays. See
// useDimStyleEditorDialog for the full explanation: `?? []` inside a
// Zustand selector allocates a fresh array on every store update,
// defeating Object.is bail-out and causing spurious re-renders.
const EMPTY_MLEADER_STYLES: MultileaderStyle[] = [];
const EMPTY_TEXT_STYLES: TextStyle[] = [];

export interface UseMLeaderStyleEditorDialogParams {
  /**
   * Current property draft map. The hook reads `["Multileader style"]`
   * to pre-select the style currently applied on the selected entity.
   */
  propertyDraft: Record<string, string>;
}

export interface UseMLeaderStyleEditorDialogResult {
  /** Whether to mount `<MultileaderStyleEditorDialog>`. */
  isOpen: boolean;
  /**
   * Whether at least one mleader style exists. The orchestrator should
   * gate dialog mounting on this — the original code did
   * `multileaderStyles?.length > 0` inline.
   */
  hasStyles: boolean;
  /**
   * Props bundle for `<MultileaderStyleEditorDialog>`. Only meaningful
   * when `isOpen && hasStyles && currentStyle`.
   */
  props: {
    style: MultileaderStyle;
    textStyleNames: string[];
    multileaderStyleNames: string[];
    cannoscaleName: string;
    onSave: (updates: Partial<MultileaderStyle>) => void;
    onCreateStyle: (
      name: string,
      basedOn?: string,
    ) => { ok: true } | { ok: false; error: string };
    onDeleteStyle: (
      name: string,
    ) => { ok: true } | { ok: false; error: string };
    onSelectStyle: (name: string) => void;
    onAnnotativePropagation: AnnotativeStylePropagationHandler;
    onClose: () => void;
  } | null;
  /** Imperative open — used by quickbar / properties view. */
  open: () => void;
  /** Imperative close — same as `props.onClose`, exposed for symmetry. */
  close: () => void;
}

export function useMLeaderStyleEditorDialog({
  propertyDraft,
}: UseMLeaderStyleEditorDialogParams): UseMLeaderStyleEditorDialogResult {
  const updateMultileaderStyle = useEditorStore(
    (store) => store.updateMultileaderStyle,
  );
  const addMultileaderStyle = useEditorStore(
    (store) => store.addMultileaderStyle,
  );
  const deleteMultileaderStyle = useEditorStore(
    (store) => store.deleteMultileaderStyle,
  );
  const recordAnnotativeStylePropagation = useEditorStore(
    (store) => store.recordAnnotativeStylePropagation,
  );
  const multileaderStyles = useEditorStore(
    (store) =>
      store.geometryDocument.styleTables?.multileaderStyles ??
      EMPTY_MLEADER_STYLES,
  );
  const textStyles = useEditorStore(
    (store) =>
      store.geometryDocument.styleTables?.textStyles ?? EMPTY_TEXT_STYLES,
  );
  const cannoscaleName = useEditorStore(
    (store) => store.geometryDocument.cannoscaleName,
  );

  // Mirrors `editingDimStyleName` in `useDimStyleEditorDialog`. null =
  // fall back to the currently applied style on the selected entity.
  const [editingMleaderStyleName, setEditingMleaderStyleName] = useState<
    string | null
  >(null);

  const { isOpen, open, close } = useDialogOpenState({
    onClose: () => setEditingMleaderStyleName(null),
  });

  const hasStyles = multileaderStyles.length > 0;

  const currentStyle = useMemo<MultileaderStyle | null>(() => {
    if (!hasStyles) return null;
    const targetName =
      editingMleaderStyleName ??
      propertyDraft["Multileader style"] ??
      multileaderStyles[0]?.name;
    return (
      multileaderStyles.find((s) => s.name === targetName) ??
      multileaderStyles[0] ??
      null
    );
  }, [hasStyles, editingMleaderStyleName, propertyDraft, multileaderStyles]);

  const onSave = useCallback(
    (updates: Partial<MultileaderStyle>) => {
      if (currentStyle?.name) {
        updateMultileaderStyle(currentStyle.name, updates);
      }
    },
    [currentStyle, updateMultileaderStyle],
  );

  const onCreateStyle = useCallback(
    (name: string, basedOn?: string) => {
      const result = addMultileaderStyle(name, basedOn);
      return result.ok
        ? ({ ok: true } as const)
        : ({ ok: false, error: result.error } as const);
    },
    [addMultileaderStyle],
  );

  const onDeleteStyle = useCallback(
    (name: string) => {
      const result = deleteMultileaderStyle(name);
      return result.ok
        ? ({ ok: true } as const)
        : ({ ok: false, error: result.error } as const);
    },
    [deleteMultileaderStyle],
  );

  const onSelectStyle = useCallback((name: string) => {
    setEditingMleaderStyleName(name);
  }, []);

  const onAnnotativePropagation = useCallback(
    ({ direction }: { direction: "enable" | "disable" }) => {
      if (currentStyle?.name) {
        recordAnnotativeStylePropagation({
          styleKind: "mleaderstyle",
          styleName: currentStyle.name,
          direction,
          cannoscaleName,
        });
      }
    },
    [currentStyle, recordAnnotativeStylePropagation, cannoscaleName],
  );

  const props =
    hasStyles && currentStyle
      ? {
          style: currentStyle,
          textStyleNames: textStyles.map((s) => s.name),
          multileaderStyleNames: multileaderStyles.map((s) => s.name),
          cannoscaleName,
          onSave,
          onCreateStyle,
          onDeleteStyle,
          onSelectStyle,
          onAnnotativePropagation,
          onClose: close,
        }
      : null;

  return {
    isOpen,
    hasStyles,
    props,
    open,
    close,
  };
}
